import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from "recharts";
import { TrendingUp, Users, Target } from "lucide-react";

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

const formatCurrency = (amount) => {
  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    return `${(amount / 1000).toFixed(0)}K`;
  }
  return amount.toString();
};

const MONTH_NAMES = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await apiClient.get("/dashboard/analytics");
      setData(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Chargement...</div>
      </div>
    );
  }

  if (!data) return null;

  const { ca_par_client, ca_mensuel, pipeline } = data;

  // Transform monthly data for chart
  const monthlyData = ca_mensuel.map(item => ({
    mois: MONTH_NAMES[item.mois - 1],
    ca: item.ca,
  }));

  // Calculate pipeline total for percentage
  const pipelineTotal = pipeline.lead + pipeline.prospect + pipeline.client;
  const pipelineData = [
    { name: "Leads", value: pipeline.lead, color: "#6B7280" },
    { name: "Prospects", value: pipeline.prospect, color: "#3B82F6" },
    { name: "Clients", value: pipeline.client, color: "#10B981" },
  ];

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white tracking-tight">Analytics</h1>
        <p className="text-zinc-500 mt-1">Tendances et indicateurs de performance</p>
      </div>

      {/* Main Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly CA Bar Chart */}
        <Card className="bg-[#121214] border-zinc-800" data-testid="chart-ca-mensuel">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-400" />
              CA Mensuel {new Date().getFullYear()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis dataKey="mois" stroke="#71717A" fontSize={12} />
                  <YAxis stroke="#71717A" fontSize={12} tickFormatter={formatCurrency} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#18181B',
                      border: '1px solid #27272A',
                      borderRadius: '8px',
                    }}
                    labelStyle={{ color: '#E5E7EB' }}
                    formatter={(value) => [`${value.toLocaleString()} FCFA`, "CA"]}
                  />
                  <Bar dataKey="ca" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Pipeline Pie Chart */}
        <Card className="bg-[#121214] border-zinc-800" data-testid="chart-pipeline">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-400" />
              Pipeline Commercial
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] flex items-center">
              <div className="w-1/2">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={pipelineData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pipelineData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#18181B',
                        border: '1px solid #27272A',
                        borderRadius: '8px',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/2 space-y-4">
                {pipelineData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: item.color }} 
                      />
                      <span className="text-zinc-300">{item.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-mono text-white font-bold">{item.value}</span>
                      <span className="text-zinc-500 text-sm ml-2">
                        ({pipelineTotal > 0 ? ((item.value / pipelineTotal) * 100).toFixed(0) : 0}%)
                      </span>
                    </div>
                  </div>
                ))}
                <div className="pt-4 border-t border-zinc-800">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400">Total</span>
                    <span className="font-mono text-white font-bold">{pipelineTotal}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CA par Client */}
      <Card className="bg-[#121214] border-zinc-800" data-testid="chart-ca-client">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-amber-400" />
            Top Clients par CA
          </CardTitle>
        </CardHeader>
        <CardContent>
          {ca_par_client.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              Aucune donnée disponible
            </div>
          ) : (
            <div className="space-y-4">
              {ca_par_client.slice(0, 8).map((item, index) => {
                const maxCA = ca_par_client[0]?.ca || 1;
                const percentage = (item.ca / maxCA) * 100;
                
                return (
                  <div key={item.client} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-mono text-zinc-500 w-6">#{index + 1}</span>
                        <span className="text-zinc-200 truncate max-w-[200px]">{item.client}</span>
                      </div>
                      <span className="font-mono text-white font-medium">
                        {item.ca.toLocaleString()} FCFA
                      </span>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${percentage}%`,
                          backgroundColor: COLORS[index % COLORS.length]
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Insights Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#121214] border-zinc-800">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Taux de conversion</p>
            <p className="text-3xl font-heading font-bold text-white">
              {pipelineTotal > 0 ? ((pipeline.client / pipelineTotal) * 100).toFixed(1) : 0}%
            </p>
            <p className="text-sm text-zinc-500 mt-1">Leads → Clients</p>
          </CardContent>
        </Card>

        <Card className="bg-[#121214] border-zinc-800">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">CA Moyen / Client</p>
            <p className="text-2xl font-mono font-bold text-white">
              {pipeline.client > 0 && ca_par_client.length > 0
                ? formatCurrency(ca_par_client.reduce((sum, c) => sum + c.ca, 0) / pipeline.client)
                : 0} FCFA
            </p>
            <p className="text-sm text-zinc-500 mt-1">Moyenne par client actif</p>
          </CardContent>
        </Card>

        <Card className="bg-[#121214] border-zinc-800">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Prospects actifs</p>
            <p className="text-3xl font-heading font-bold text-blue-400">{pipeline.prospect}</p>
            <p className="text-sm text-zinc-500 mt-1">En cours de conversion</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
